// Outlet context for AdminShell — a discriminated union over the shell's two
// modes (ADR-0036).
//
// Single-station mode carries the station's `config`; aggregated mode
// (/admin/all/*) carries the list of administrable stations and no config,
// because `PublisherConfig` is one station's COMMERCE configuration
// (revSharePercent, serviceFeeRates, minBudget, allowedDmaCodes, products)
// and there is no honest value for any of those under "all stations".
//
// Scope-flexible pages (Overview, Submissions, Advertisers) read via
// useAdminContext() and switch on `ctx.mode`. Every station-bound page reads
// via useSingleStationContext(), which throws rather than rendering — that
// throw is what stands between "forgot to handle aggregated mode" and
// "silently rendered the wrong station's data".

import { useOutletContext } from 'react-router-dom';
import type { PublisherConfig } from '../../../types/selfService';
import type { AdminStation } from '../auth/useAuth';

// Loose mirror of useAuth's SelfServiceUser. Kept structural so the shell
// doesn't need to import the full type — pages that already use useAuth
// for additional fields can keep doing so.
export interface AdminUser {
  id: string;
  email: string;
  rawRole?: string;
}

export type AdminContext =
  | { mode: 'single'; config: PublisherConfig; slug: string; user: AdminUser }
  // `stations` is AdminStation[], not PublisherConfig[]: useAuth already
  // holds exactly this, fetched once on session change, so aggregated mode
  // costs zero extra requests. Full configs would cost N fetches for data no
  // aggregated page reads.
  //
  // `collapseSlug` is the Scope collapse target — the station a click on a
  // station-bound tab resolves to. Always a real slug, never 'all'.
  | {
      mode: 'aggregated';
      stations: AdminStation[];
      collapseSlug: string;
      user: AdminUser;
    };

export function useAdminContext(): AdminContext {
  return useOutletContext<AdminContext>();
}

// Convenience for the >90% case of "I only handle single-station mode."
// Throws (instead of silently returning a stale value) when accessed from
// an aggregated route. Station-bound tabs never legitimately reach this:
// the sidebar's hrefs encode the collapse and AdminShell redirects a typed
// /admin/all/<station-bound-tab>, so a throw here means a route was added
// without deciding which kind of tab it is.
export function useSingleStationContext() {
  const ctx = useOutletContext<AdminContext>();
  if (ctx.mode !== 'single') {
    throw new Error(
      'This page expects single-station context. ' +
        'Add an aggregated-mode branch in the page, or add the route to STATION_BOUND_TABS so /admin/all collapses it.',
    );
  }
  return ctx;
}
